<script setup>
import { computed, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const MAX_OFFSET_MINUTES = 999 * 24 * 60; // 999 days

const { t } = useI18n();

const normalizedParams = computed(() => {
  const value = props.modelValue;
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'object' && first !== null ? first : {};
  }
  return typeof value === 'object' && value !== null ? value : {};
});

// Relative offset from when the rule runs. 0 = now (the rule's execution moment).
const offsetMinutes = computed({
  get: () => normalizedParams.value.offset_minutes ?? 0,
  set: value => {
    const numValue = Math.min(
      Math.max(0, Number(value) || 0),
      MAX_OFFSET_MINUTES
    );
    emit('update:modelValue', [
      { ...normalizedParams.value, offset_minutes: numValue },
    ]);
  },
});

const offsetUnit = ref(DURATION_UNITS.DAYS);

const detectUnit = minutes => {
  const m = Number(minutes) || 0;
  if (m === 0) return DURATION_UNITS.DAYS;
  if (m % (24 * 60) === 0) return DURATION_UNITS.DAYS;
  if (m % 60 === 0) return DURATION_UNITS.HOURS;
  return DURATION_UNITS.MINUTES;
};

onBeforeMount(() => {
  offsetUnit.value = detectUnit(normalizedParams.value.offset_minutes ?? 0);
});
</script>

<template>
  <div class="flex flex-col gap-1">
    <label class="text-xs text-n-slate-11">
      {{ t('AUTOMATION.ACTION.RELATIVE_DATE_LABEL') }}
    </label>
    <div class="flex items-center gap-2">
      <!-- allow 0 (now) to 999 days from when the rule runs -->
      <DurationInput
        v-model:model-value="offsetMinutes"
        v-model:unit="offsetUnit"
        :min="0"
        :max="MAX_OFFSET_MINUTES"
      />
    </div>
  </div>
</template>
